import { useState } from "react";
import { Field, FormSpy } from "react-final-form";
import { TitlePage, RadioField } from "~/components";
import {
  composeValidators,
  disabledValidate,
  notEmptyValue,
  required,
  testRegExp
} from "~/utils/validate";
import { Divider } from "antd";
import {
  Wizard,
  FormField,
  CustomInnField,
  FormFieldAutocompleteAddress,
  FormFieldBic
} from "~/pages/questionnaires/shared";
import cn from "classnames";
import { ClientId, NamesOfFieldEnum, OrgTypeEnum } from "~/typings/types";
import { configTitleFieldsOfBank, configTitleFieldsOfCompany } from "../configTitleFields";
import { GET_BANK_INFO, PERSONAL_DATA_ADDRESS_SUGGEST } from "~/pages/questionnaires/v1/customer/api/apiUrls";

import styles from "~/pages/questionnaires/shared/Shared.module.scss";

interface Props {
  clientId: ClientId;
  orgType: OrgTypeEnum;
}

export const CompanyDetails = ({ clientId, orgType }: Props) => {
  /** До проверки ИНН все поля в этом шаге должны быть disable */
  const [isDisabledFieldsByInn, setIsDisabledFieldsByInn] = useState(true);

  return (
    <Wizard.Page>
      <TitlePage name="Укажите свои реквизиты" number="3" />
      <Divider style={{ margin: 0 }} />
      <div className={styles.container}>
        <CustomInnField
          innLength={10}
          orgType={orgType}
          clientId={clientId}
          configNameFields={configTitleFieldsOfCompany}
          // setIsDisabledFieldsByInn={setIsDisabledFieldsByInn}
        />

        <FormField
          titleField={configTitleFieldsOfCompany[NamesOfFieldEnum.Name]}
          name={NamesOfFieldEnum.Name}
          required
          validate={composeValidators(required())}
          disabled={isDisabledFieldsByInn}
        />

        <FormField
          titleField={configTitleFieldsOfCompany[NamesOfFieldEnum.Ogrn]}
          name={NamesOfFieldEnum.Ogrn}
          maxLength={13}
          required
          validate={composeValidators(
            required(),
            testRegExp(
              /^[15]\d{12}$/g,
              'ОГРН должен начинаться на "1" или "5" и состоять из 13 цифр'
            )
          )}
          disabled={isDisabledFieldsByInn}
        />

        <FormField
          titleField={configTitleFieldsOfCompany[NamesOfFieldEnum.Kpp]}
          name={NamesOfFieldEnum.Kpp}
          maxLength={9}
          required
          validate={composeValidators(
            required(),
            testRegExp(/^(\d){9}$/g, "КПП должен состоять из 9 цифр")
          )}
          disabled={isDisabledFieldsByInn}
        />

        <FormSpy>
          {propsSpy => {
            return (
              <FormFieldBic
                titleField={configTitleFieldsOfCompany[NamesOfFieldEnum.BankBic]}
                name={NamesOfFieldEnum.BankBic}
                required
                validate={composeValidators(
                  required(),
                  testRegExp(/^04(\d){7}$/g, 'БИК должен начинаться на "04" и состоять из 9 цифр')
                )}
                disabled={isDisabledFieldsByInn}
                configNameFieldsModalSuggestion={configTitleFieldsOfBank}
                propsSpy={propsSpy}
                clientId={clientId}
                tooltip="БИК — уникальный код банка. Он состоит из девяти цифр"
                urlGetBankInfo={GET_BANK_INFO}
              />
            );
          }}
        </FormSpy>

        <FormField
          titleField={configTitleFieldsOfCompany[NamesOfFieldEnum.BankName]}
          name={NamesOfFieldEnum.BankName}
          required
          validate={composeValidators(required())}
          disabled={isDisabledFieldsByInn}
        />

        <FormField
          titleField={configTitleFieldsOfCompany[NamesOfFieldEnum.SettlementAccount]}
          name={NamesOfFieldEnum.SettlementAccount}
          maxLength={20}
          mask="407"
          required
          validate={composeValidators(
            required(),
            testRegExp(
              /^407(\d){17}$/g,
              'Номер расчётного счёта должен начинаться на "407" и состоять из 20 цифр'
            )
          )}
          disabled={isDisabledFieldsByInn}
        />

        <FormField
          titleField={configTitleFieldsOfCompany[NamesOfFieldEnum.CorrespondentAccount]}
          name={NamesOfFieldEnum.CorrespondentAccount}
          maxLength={20}
          mask="301"
          required
          validate={composeValidators(
            required(),
            testRegExp(
              /^301(\d){17}$/g,
              'Номер корреспондентского счёта должен начинаться на "301" и состоять из 20 цифр'
            )
          )}
          disabled={isDisabledFieldsByInn}
        />

        <FormSpy>
          {propsSpy => {
            return (
              <FormFieldAutocompleteAddress
                titleField={configTitleFieldsOfCompany[NamesOfFieldEnum.LegalAddress]}
                name={NamesOfFieldEnum.LegalAddress}
                nameFiasId={NamesOfFieldEnum.LegalAddressFiasId}
                required
                validate={composeValidators(required(), notEmptyValue())}
                disabled={isDisabledFieldsByInn}
                propsSpy={propsSpy}
                clientId={clientId}
                urlPersonalDataAddressSuggest={PERSONAL_DATA_ADDRESS_SUGGEST}
              />
            );
          }}
        </FormSpy>

        <FormSpy>
          {propsSpy => {
            return (
              <FormFieldAutocompleteAddress
                titleField={configTitleFieldsOfCompany[NamesOfFieldEnum.PostalAddress]}
                name={NamesOfFieldEnum.PostalAddress}
                nameFiasId={NamesOfFieldEnum.PostalAddressFiasId}
                tooltip="При необходимости направим оригиналы документов на этот адрес"
                disabled={isDisabledFieldsByInn}
                propsSpy={propsSpy}
                clientId={clientId}
                validate={composeValidators(notEmptyValue())}
                triggerAutofill={[NamesOfFieldEnum.PostalAddress, NamesOfFieldEnum.LegalAddress]}
                triggerAutofillForAddressFiasId={[
                  NamesOfFieldEnum.PostalAddressFiasId,
                  NamesOfFieldEnum.LegalAddressFiasId
                ]}
                urlPersonalDataAddressSuggest={PERSONAL_DATA_ADDRESS_SUGGEST}
              />
            );
          }}
        </FormSpy>

        <FormField
          titleField={configTitleFieldsOfCompany[NamesOfFieldEnum.Vat]}
          name={NamesOfFieldEnum.Vat}
          required
        >
          <div className={cn(styles.containerRadio, styles.vatRadioContainer)}>
            <Field
              name={NamesOfFieldEnum.Vat}
              type="radio"
              value="no"
              component={props => (
                <RadioField  {...props} disabled={isDisabledFieldsByInn} />
              )}
              validate={disabledValidate(composeValidators(required()))}
            >
              Нет
            </Field>
            <Field
              name={NamesOfFieldEnum.Vat}
              type="radio"
              value="0"
              component={props => (
                <RadioField  {...props} disabled={isDisabledFieldsByInn} />
              )}
              validate={disabledValidate(composeValidators(required()))}
            >
              Да, ставка 0%
            </Field>
            <Field
              name={NamesOfFieldEnum.Vat}
              type="radio"
              value="10"
              component={props => (
                <RadioField  {...props} disabled={isDisabledFieldsByInn} />
              )}
              validate={disabledValidate(composeValidators(required()))}
            >
              Да, ставка 10%
            </Field>
            <Field
              name={NamesOfFieldEnum.Vat}
              type="radio"
              value="20"
              component={props => (
                <RadioField  {...props} disabled={isDisabledFieldsByInn} />
              )}
              validate={disabledValidate(composeValidators(required()))}
            >
              Да, ставка 20%
            </Field>
          </div>
        </FormField>

        <FormField
          titleField={configTitleFieldsOfCompany[NamesOfFieldEnum.SignatureInfo]}
          name={NamesOfFieldEnum.SignatureInfo}
          required
          validate={composeValidators(required())}
          tooltip="При необходимости направим оригиналы документов на этот адрес"
          triggerAutofill={[NamesOfFieldEnum.SignatureInfo, NamesOfFieldEnum.CrmName]}
          disabled={isDisabledFieldsByInn}
        />

      </div>
    </Wizard.Page>
  );
};
